import { useCallback, useRef, useState } from 'react';
import debounce from 'lodash/debounce';

import { recipeV2Client } from '../../../app/services/api/recipeV2';
import { StockItemWithRecipeFlag } from '../../Recipe/hooks/useStockItemsData';

export const splitRecipeV2SearchResults = (results: StockItemWithRecipeFlag[]) => ({
  stockItems: results.filter((result) => !result.isRecipe),
  recipeItems: results.filter((result) => result.isRecipe),
});

const useRecipeV2ComponentSearch = () => {
  const [stockItems, setStockItems] = useState<StockItemWithRecipeFlag[]>([]);
  const [recipeItems, setRecipeItems] = useState<StockItemWithRecipeFlag[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const requestIdRef = useRef(0);

  const debouncedSearch = useCallback(
    debounce(async (search: string, _storeNumber: string) => {
      const requestId = ++requestIdRef.current;

      if (!search.trim()) {
        setStockItems([]);
        setRecipeItems([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);

      try {
        const results = await recipeV2Client.searchStockItems(search);

        if (requestId !== requestIdRef.current) return;

        const splitResults = splitRecipeV2SearchResults(results);
        setStockItems(splitResults.stockItems);
        setRecipeItems(splitResults.recipeItems);
      } catch (error) {
        if (requestId !== requestIdRef.current) return;

        console.error(error);
        setStockItems([]);
        setRecipeItems([]);
      } finally {
        if (requestId === requestIdRef.current) {
          setIsSearching(false);
        }
      }
    }, 500),
    [],
  );

  return {
    stockItems,
    setStockItems,
    recipeItems,
    isSearching,
    debouncedSearch,
  };
};

export default useRecipeV2ComponentSearch;
